import { useMemo, useState, useEffect } from "react";
import {
  FolderTree,
  Pencil,
  ChevronUp,
  ChevronDown,
  ChevronRight,
  X,
  Check,
  Eye,
  List,
  Calendar,
  FileText,
  Info,
  Repeat,
  GitBranch,
  Folder,
  File,
  Upload,
  Plus,
  Trash2,
  Lock,
  Unlock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useStudyBuilderStore, selectIsReadOnly } from "@/stores/study-builder-store";
import { useUpdateBuildArtifact, useProtocol } from "@/hooks/use-protocol-builder";
import { ArtifactReviewBar } from "./ArtifactReviewBar";
import { CrfFieldEditor } from "./CrfFieldEditor";
import { ReviewCommentThread } from "./ReviewCommentThread";
import { StudyBuildFlowchart } from "./StudyBuildFlowchart";
import { VersionBadge } from "./VersionBadge";
import { ProtocolVersionHistory } from "./ProtocolVersionHistory";
import {
  ProtocolTipTapEditor,
  useProtocolDocument,
} from "./protocol-editor";
import { DocumentEditor } from "./DocumentEditor";
import { computeEtmfCompletion } from "./etmf-utils";
import { suggestEtmfFilingFn } from "@/api/client";
import { suggestFolder } from "./etmf-utils";
import type {
  ArtifactType,
  CrfField,
  CrfSection,
  CrfDefinition,
  VisitDefinition,
} from "@/api/protocol-types";

function LockButton({
  artifactType,
  artifactId,
  locked,
}: {
  artifactType: string;
  artifactId: string;
  locked?: boolean;
}) {
  const buildId = useStudyBuilderStore((s) => s.buildId);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);
  const { mutate: updateArtifact, isPending } = useUpdateBuildArtifact();

  if (isReadOnly || !buildId) return null;

  const toggle = () => {
    updateArtifact({
      buildId,
      artifactType,
      artifactId,
      updates: {
        locked: !locked,
        ...(locked ? { lockedBy: null, lockedAt: null } : { lockedAt: new Date().toISOString() }),
      },
    });
  };

  return (
    <button
      onClick={toggle}
      disabled={isPending}
      className={cn(
        "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors",
        locked
          ? "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )}
      title={locked ? "Unlock — allow AI to modify" : "Lock — prevent AI from modifying"}
    >
      {locked ? <Lock className="h-3 w-3" /> : <Unlock className="h-3 w-3" />}
      {locked ? "Locked" : "Lock"}
    </button>
  );
}

export function ArtifactContent({
  type,
  artifactId,
}: {
  type: ArtifactType;
  artifactId: string;
}) {
  switch (type) {
    case "overview":
      return <OverviewContent />;
    case "protocol":
      return <ProtocolOrDocumentContent artifactId={artifactId} />;
    case "crf":
      return <CrfContent artifactId={artifactId} />;
    case "visit":
      return <VisitContent artifactId={artifactId} />;
    case "arm":
      return <ArmContent />;
    case "eligibility":
      return <EligibilityContent />;
    case "endpoint":
      return <EndpointContent />;
    case "etmf":
      return <EtmfContent />;
    case "protocol_synopsis":
    case "resource_plan":
    case "rbm_plan":
    case "safety_management_plan":
    case "training_plan":
    case "raci_matrix":
      return <DocumentEditor type={type} id={artifactId} />;
    default:
      return <OverviewContent />;
  }
}

// ── Study Documents ──────────────────────────────────

const DOC_TYPE_LABELS: Record<string, string> = {
  protocol: "Protocol Document",
  protocol_synopsis: "Protocol Synopsis",
  resource_plan: "Resource Plan",
  rbm_plan: "Risk-Based Monitoring Plan",
  safety_management_plan: "Safety Management Plan",
  training_plan: "Training Plan",
  raci_matrix: "RACI Matrix",
};

// @ts-ignore legacy
function StudyDocumentContent({ type, id }: { type: string; id: string }) {
  const studyDocuments = useStudyBuilderStore((s) => s.studyDocuments);
  const doc = studyDocuments.find((d) => d.id === id) ?? studyDocuments.find((d) => d.documentType === type);

  if (!doc) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        No {DOC_TYPE_LABELS[type] ?? "document"} generated yet.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{doc.title}</h2>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600">
            {doc.status}
          </span>
          <LockButton artifactType={type} artifactId={doc.id} locked={doc.locked} />
        </div>
      </div>

      {doc.sections.map((section) => (
        <div key={section.id} className="space-y-2">
          <h3 className="text-sm font-semibold text-foreground border-b border-border pb-1">
            {section.title}
          </h3>

          {section.content && (
            <div className="prose prose-sm max-w-none text-muted-foreground whitespace-pre-wrap">
              {section.content}
            </div>
          )}

          {section.tableData && section.tableData.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-border">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-muted/50">
                    {Object.keys(section.tableData[0]).map((col) => (
                      <th key={col} className="px-3 py-2 text-left font-medium text-foreground capitalize">
                        {col.replace(/_/g, " ")}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {section.tableData.map((row, i) => (
                    <tr key={i} className="border-t border-border">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="px-3 py-2 text-muted-foreground">
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Overview ──────────────────────────────────────────

function OverviewContent() {
  const build = useStudyBuilderStore((s) => s.build);
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const visits = useStudyBuilderStore((s) => s.visits);
  const arms = useStudyBuilderStore((s) => s.arms);
  const eligibilityCriteria = useStudyBuilderStore(
    (s) => s.eligibilityCriteria,
  );
  const endpoints = useStudyBuilderStore((s) => s.endpoints);
  const etmfFolders = useStudyBuilderStore((s) => s.etmfFolders);

  if (!build) {
    return (
      <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
        No study build data yet. Start a conversation to design your study.
      </div>
    );
  }

  const stats = [
    { label: "CRFs", value: crfs.length },
    { label: "Visits", value: visits.length },
    { label: "Arms", value: arms.length },
    { label: "Criteria", value: eligibilityCriteria.length },
    { label: "Endpoints", value: endpoints.length },
    { label: "eTMF", value: etmfFolders.length },
  ];

  return (
    <div className="flex flex-col gap-4 p-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">
          {build.studyTitle}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">
          {build.protocolNumber} &middot; Phase {build.phase}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col rounded-lg border border-border bg-card p-3"
          >
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              {stat.label}
            </span>
            <span className="mt-0.5 text-lg font-bold tabular-nums text-foreground">
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {build.aiProcessingDisclaimer && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
          <p className="text-[11px] text-amber-700 dark:text-amber-400">
            {build.aiProcessingDisclaimer}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Protocol Switcher (Document Editor or TipTap viewer) ──

function ProtocolOrDocumentContent({ artifactId }: { artifactId: string }) {
  const studyDocuments = useStudyBuilderStore((s) => s.studyDocuments);
  const protocolDoc = studyDocuments.find((d) => d.id === artifactId) ?? studyDocuments.find((d) => d.documentType === "protocol");

  // If there's an AI-generated protocol document, show the DocumentEditor
  if (protocolDoc) {
    return <DocumentEditor type="protocol" id={protocolDoc.id} />;
  }

  // Otherwise show the original parsed protocol view
  return <ProtocolContent />;
}

// ── Protocol (Parsed Upload View) ──────────────────────

export function ProtocolContent() {
  const build = useStudyBuilderStore((s) => s.build);
  const arms = useStudyBuilderStore((s) => s.arms);
  const eligibilityCriteria = useStudyBuilderStore((s) => s.eligibilityCriteria);
  const endpoints = useStudyBuilderStore((s) => s.endpoints);
  const visits = useStudyBuilderStore((s) => s.visits);
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const highlightTarget = useStudyBuilderStore((s) => s.highlightTarget);
  const clearHighlightTarget = useStudyBuilderStore((s) => s.clearHighlightTarget);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);

  const [editorMode, setEditorMode] = useState<"view" | "edit">("view");
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const protocolDoc = useProtocolDocument();
  const { data: protocolData } = useProtocol(build?.protocolId);

  // Scroll-to-highlight when highlightTarget is set
  useEffect(() => {
    if (!highlightTarget) return;

    let elementId: string;
    if (highlightTarget.type === "section") {
      elementId = highlightTarget.id;
    } else {
      elementId = `protocol-${highlightTarget.type}-${highlightTarget.id}`;
    }
    const fallbackId =
      highlightTarget.type !== "section"
        ? `protocol-section-${highlightTarget.type}`
        : undefined;

    const timeout = setTimeout(() => {
      const el =
        document.getElementById(elementId) ??
        (fallbackId ? document.getElementById(fallbackId) : null);
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          block: highlightTarget.type === "section" ? "start" : "center",
        });
        el.classList.add("protocol-highlight-flash");
        setTimeout(() => {
          el.classList.remove("protocol-highlight-flash");
          clearHighlightTarget();
        }, 2000);
      } else {
        clearHighlightTarget();
      }
    }, 150);
    return () => clearTimeout(timeout);
  }, [highlightTarget, clearHighlightTarget]);

  if (!build) {
    return <EmptyState text="No protocol data yet" />;
  }

  const inclusion = eligibilityCriteria.filter((c) => c.type === "inclusion");
  const exclusion = eligibilityCriteria.filter((c) => c.type === "exclusion");

  const groupedEndpoints: Record<string, typeof endpoints> = {};
  for (const ep of endpoints) {
    if (!groupedEndpoints[ep.type]) groupedEndpoints[ep.type] = [];
    groupedEndpoints[ep.type].push(ep);
  }
  const typeOrder = ["primary", "secondary", "exploratory", "safety"];

  const primaryEndpoint = endpoints.find((e) => e.type === "primary");

  // Static section definitions for TOC
  const tocSections = [
    { number: "1.0", title: "Protocol Synopsis", id: "protocol-section-synopsis" },
    { number: "2.0", title: "Study Flow Diagram", id: "protocol-section-flowchart" },
    { number: "3.0", title: "Study Objective", id: "protocol-section-objective" },
    { number: "4.0", title: "Study Design and Endpoints", id: "protocol-section-design" },
    { number: "5.0", title: "Patient Population and Eligibility", id: "protocol-section-eligibility" },
    { number: "6.0", title: "Study Procedures", id: "protocol-section-procedures" },
    { number: "7.0", title: "Treatment Arms", id: "protocol-section-arms" },
  ];

  const [activeSectionId, setActiveSectionId] = useState(tocSections[0].id);

  return (
    <div className="flex h-full">
      {/* Left TOC sidebar */}
      <div className="flex w-56 shrink-0 flex-col border-r border-border">
        <div className="border-b border-border p-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sections</span>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {tocSections.map((s) => (
            <button
              key={s.id}
              onClick={() => {
                setActiveSectionId(s.id);
                document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={cn(
                "flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors",
                activeSectionId === s.id
                  ? "bg-primary/10 text-primary"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground",
              )}
            >
              <span className="shrink-0 text-xs font-semibold tabular-nums w-6">{s.number}</span>
              <span className="truncate text-xs font-medium">{s.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Right protocol content */}
      <div className="flex-1 overflow-y-auto">
      <div className="min-h-full bg-muted/30 py-8 px-4 md:px-8">
      {/* Highlight animation styles */}
      <style>{`
        @keyframes protocolHighlight {
          0% { box-shadow: 0 0 0 0 hsl(var(--primary) / 0.4); background-color: hsl(var(--primary) / 0.08); }
          50% { box-shadow: 0 0 0 4px hsl(var(--primary) / 0.2); background-color: hsl(var(--primary) / 0.12); }
          100% { box-shadow: 0 0 0 0 transparent; background-color: transparent; }
        }
        .protocol-highlight-flash {
          animation: protocolHighlight 2s ease-out;
          border-radius: 0.25rem;
        }
      `}</style>

      <div className="flex items-center justify-between mb-2">
        <ArtifactReviewBar artifactType="protocol" artifactId="protocol" />
        <div className="flex items-center gap-2">
          {protocolData?.versions && protocolData.versions.length > 0 && (
            <VersionBadge
              versionNumber={
                protocolData.versions[protocolData.versions.length - 1]
                  .versionNumber
              }
              onClick={() => setShowVersionHistory(!showVersionHistory)}
            />
          )}
          {!isReadOnly && (
            <button
              onClick={() => setEditorMode(editorMode === "view" ? "edit" : "view")}
              className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Pencil className="h-3 w-3" />
              {editorMode === "view" ? "Edit Document" : "View Mode"}
            </button>
          )}
        </div>
      </div>

      {editorMode === "edit" ? (
        <div className="mt-4">
          <ProtocolTipTapEditor initialContent={protocolDoc} />
        </div>
      ) : (
      /* ── Document page (static view) ──────────────────────── */
      <div className="mx-auto max-w-[816px] bg-white dark:bg-card shadow-lg border border-border rounded-sm mt-4">
        {/* Running header */}
        <div className="flex items-center justify-between border-b border-border px-8 md:px-12 py-3 text-[11px] text-muted-foreground">
          <span className="font-medium">{build.protocolNumber}</span>
          <span className="truncate mx-4 text-center">{build.studyTitle}</span>
          <span className="shrink-0 uppercase tracking-wider">Confidential</span>
        </div>

        <div className="px-8 md:px-12 py-10">
          {/* ── TITLE PAGE ───────────────────────── */}
          <div className="text-center mb-12">
            <h1 className="text-2xl font-bold text-foreground leading-tight">
              {build.studyTitle}
            </h1>
            <p className="mt-3 text-sm text-muted-foreground">
              {build.indication && <>A Phase {build.phase} Study &middot; {build.indication}</>}
              {!build.indication && <>Phase {build.phase} Clinical Study Protocol</>}
            </p>
            <div className="mx-auto mt-6 h-px w-32 bg-border" />
            <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <span>Protocol: {build.protocolNumber}</span>
              {build.sponsor && <span>Sponsor: {build.sponsor}</span>}
            </div>
            {(build.therapeuticArea || build.estimatedSampleSize != null || build.studyDuration) && (
              <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-1 text-sm text-muted-foreground">
                {build.therapeuticArea && <span>{build.therapeuticArea}</span>}
                {build.estimatedSampleSize != null && <span>N={build.estimatedSampleSize}</span>}
                {build.studyDuration && <span>Duration: {build.studyDuration}</span>}
              </div>
            )}
          </div>

          {/* ── TABLE OF CONTENTS ─────────────────── */}
          <div className="mb-12">
            <h2 className="text-lg font-bold text-foreground mb-4 uppercase tracking-wide text-center">
              Table of Contents
            </h2>
            <div className="flex flex-col gap-1.5">
              {tocSections.map((s) => (
                <div key={s.number} className="flex items-baseline gap-2 text-sm text-foreground">
                  <span className="font-semibold tabular-nums w-8 shrink-0">{s.number}</span>
                  <span className="uppercase text-xs font-semibold tracking-wide">{s.title}</span>
                  <span className="flex-1 border-b border-dotted border-muted-foreground/30 min-w-4 mx-1" />
                </div>
              ))}
            </div>
          </div>

          {/* ── 1.0 PROTOCOL SYNOPSIS ─────────────── */}
          <DocSectionHeading number="1.0" title="Protocol Synopsis" id="protocol-section-synopsis" />
          <table className="w-full border-collapse border border-border text-sm mb-2">
            <tbody>
              <SynopsisRow label="Study Title" value={build.studyTitle} />
              <SynopsisRow label="Protocol Number" value={build.protocolNumber} />
              <SynopsisRow label="Phase" value={`Phase ${build.phase}`} />
              <SynopsisRow label="Sponsor" value={build.sponsor} />
              <SynopsisRow label="Indication" value={build.indication} />
              <SynopsisRow label="Therapeutic Area" value={build.therapeuticArea} />
              <SynopsisRow label="Study Objective" value={build.studyObjective} />
              <SynopsisRow label="Study Design" value={build.studyDesign} />
              <SynopsisRow
                label="Estimated Sample Size"
                value={build.estimatedSampleSize != null ? `N=${build.estimatedSampleSize}` : undefined}
              />
              <SynopsisRow label="Study Duration" value={build.studyDuration} />
              <SynopsisRow
                label="Treatment Assignment"
                value={
                  arms.length > 0
                    ? arms.map((a) => `${a.name} (${a.type.replace("_", " ")})`).join("; ")
                    : undefined
                }
              />
              <SynopsisRow label="Primary Endpoint" value={primaryEndpoint?.name} />
              <SynopsisRow
                label="Primary Endpoint Measure"
                value={primaryEndpoint?.measure}
              />
            </tbody>
          </table>

          {/* ── 2.0 STUDY FLOW DIAGRAM ────────────── */}
          <DocSectionHeading number="2.0" title="Study Flow Diagram" id="protocol-section-flowchart" />
          {arms.length > 0 || visits.length > 0 || eligibilityCriteria.length > 0 ? (
            <div className="overflow-x-auto">
              <StudyBuildFlowchart />
            </div>
          ) : (
            <NoDataPlaceholder />
          )}

          {/* ── 3.0 STUDY OBJECTIVE ───────────────── */}
          <DocSectionHeading number="3.0" title="Study Objective" id="protocol-section-objective" />
          {build.studyObjective ? (
            <p className="text-sm leading-relaxed text-foreground">{build.studyObjective}</p>
          ) : (
            <NoDataPlaceholder />
          )}

          {/* ── 4.0 STUDY DESIGN AND ENDPOINTS ───── */}
          <DocSectionHeading number="4.0" title="Study Design and Endpoints" id="protocol-section-design" />
          {build.studyDesign ? (
            <p className="text-sm leading-relaxed text-foreground mb-4">{build.studyDesign}</p>
          ) : (
            <p className="text-sm text-muted-foreground italic mb-4">No study design description generated yet.</p>
          )}

          {endpoints.length > 0 ? (
            <>
              {typeOrder
                .filter((t) => groupedEndpoints[t]?.length)
                .map((type, idx) => (
                  <div key={type}>
                    <DocSubSectionHeading
                      number={`4.${idx + 1}`}
                      title={`${type.charAt(0).toUpperCase() + type.slice(1)} Endpoints`}
                    />
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-border text-sm">
                        <thead>
                          <tr className="bg-muted/40">
                            <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Endpoint</th>
                            <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Description</th>
                            <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Measure</th>
                            <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Timeframe</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupedEndpoints[type].map((ep) => (
                            <tr key={ep.id} id={`protocol-endpoint-${ep.id}`} className="transition-all">
                              <td className="border border-border px-3 py-2 font-medium text-foreground">{ep.name}</td>
                              <td className="border border-border px-3 py-2 text-foreground">{ep.description}</td>
                              <td className="border border-border px-3 py-2 text-foreground">{ep.measure}</td>
                              <td className="border border-border px-3 py-2 text-foreground">{ep.timeframe}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </>
          ) : !build.studyDesign ? null : (
            <p className="text-sm text-muted-foreground italic">No endpoints defined yet.</p>
          )}

          {/* ── 5.0 PATIENT POPULATION AND ELIGIBILITY ── */}
          <DocSectionHeading number="5.0" title="Patient Population and Eligibility" id="protocol-section-eligibility" />
          {eligibilityCriteria.length > 0 ? (
            <>
              {inclusion.length > 0 && (
                <>
                  <DocSubSectionHeading number="5.1" title="Inclusion Criteria" />
                  <ol className="list-decimal list-outside ml-6 flex flex-col gap-2 text-sm leading-relaxed text-foreground">
                    {inclusion.map((criterion) => (
                      <li
                        key={criterion.id}
                        id={`protocol-eligibility-${criterion.id}`}
                        className="pl-1 transition-all"
                      >
                        {criterion.text}
                      </li>
                    ))}
                  </ol>
                </>
              )}
              {exclusion.length > 0 && (
                <>
                  <DocSubSectionHeading number="5.2" title="Exclusion Criteria" />
                  <ol className="list-decimal list-outside ml-6 flex flex-col gap-2 text-sm leading-relaxed text-foreground">
                    {exclusion.map((criterion) => (
                      <li
                        key={criterion.id}
                        id={`protocol-eligibility-${criterion.id}`}
                        className="pl-1 transition-all"
                      >
                        {criterion.text}
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </>
          ) : (
            <NoDataPlaceholder />
          )}

          {/* ── 6.0 STUDY PROCEDURES ──────────────── */}
          <DocSectionHeading number="6.0" title="Study Procedures" id="protocol-section-procedures" />
          {visits.length > 0 ? (
            <>
              <DocSubSectionHeading number="6.1" title="Schedule of Events" />
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-border text-sm">
                  <thead>
                    <tr className="bg-muted/40">
                      <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">#</th>
                      <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Visit</th>
                      <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Type</th>
                      <th className="border border-border px-3 py-2 text-right font-semibold text-foreground">Study Day</th>
                      <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">Window</th>
                      <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">CRFs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visits.map((visit) => (
                      <tr key={visit.id} id={`protocol-visit-${visit.id}`} className="transition-all">
                        <td className="border border-border px-3 py-2 tabular-nums text-foreground">{visit.visitNumber}</td>
                        <td className="border border-border px-3 py-2 font-medium text-foreground">{visit.name}</td>
                        <td className="border border-border px-3 py-2 capitalize text-foreground">
                          {visit.type.replace("_", " ")}
                        </td>
                        <td className="border border-border px-3 py-2 text-right tabular-nums text-foreground">
                          {visit.studyDay ?? "\u2014"}
                        </td>
                        <td className="border border-border px-3 py-2 text-center tabular-nums text-foreground">
                          -{visit.windowDaysBefore}/+{visit.windowDaysAfter}d
                        </td>
                        <td className="border border-border px-3 py-2 text-center tabular-nums text-foreground">
                          {(visit.requiredCrfIds?.length ?? 0) + (visit.optionalCrfIds?.length ?? 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {crfs.length > 0 && (
                <>
                  <DocSubSectionHeading number="6.2" title="Data Collection" />
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-border text-sm">
                      <thead>
                        <tr className="bg-muted/40">
                          <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">CRF Name</th>
                          <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Category</th>
                          <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">Sections</th>
                          <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">Fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {crfs.map((crf) => {
                          const fieldCount = crf.sections.reduce((s, sec) => s + sec.fields.length, 0);
                          return (
                            <tr key={crf.id} id={`protocol-crf-${crf.id}`} className="transition-all">
                              <td className="border border-border px-3 py-2 font-medium text-foreground">{crf.name}</td>
                              <td className="border border-border px-3 py-2 text-foreground">{crf.category}</td>
                              <td className="border border-border px-3 py-2 text-center tabular-nums text-foreground">{crf.sections.length}</td>
                              <td className="border border-border px-3 py-2 text-center tabular-nums text-foreground">{fieldCount}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          ) : (
            <NoDataPlaceholder />
          )}

          {/* ── 7.0 TREATMENT ARMS ────────────────── */}
          <DocSectionHeading number="7.0" title="Treatment Arms" id="protocol-section-arms" />
          {arms.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-border text-sm">
                <thead>
                  <tr className="bg-muted/40">
                    <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Arm Name</th>
                    <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Type</th>
                    <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Intervention</th>
                    <th className="border border-border px-3 py-2 text-left font-semibold text-foreground">Dosage</th>
                    <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">Target N</th>
                    <th className="border border-border px-3 py-2 text-center font-semibold text-foreground">Ratio</th>
                  </tr>
                </thead>
                <tbody>
                  {arms.map((arm) => (
                    <tr key={arm.id} id={`protocol-arm-${arm.id}`} className="transition-all">
                      <td className="border border-border px-3 py-2 font-medium text-foreground">{arm.name}</td>
                      <td className="border border-border px-3 py-2 capitalize text-foreground">{arm.type.replace("_", " ")}</td>
                      <td className="border border-border px-3 py-2 text-foreground">{arm.intervention}</td>
                      <td className="border border-border px-3 py-2 text-foreground">{arm.dosage ?? "\u2014"}</td>
                      <td className="border border-border px-3 py-2 text-center tabular-nums text-foreground">
                        {arm.targetN ?? "\u2014"}
                      </td>
                      <td className="border border-border px-3 py-2 text-center text-foreground">
                        {arm.randomizationRatio ?? "\u2014"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <NoDataPlaceholder />
          )}
        </div>

        {/* Running footer */}
        <div className="flex items-center justify-between border-t border-border px-8 md:px-12 py-3 text-[10px] text-muted-foreground">
          <span>Protocol: {build.protocolNumber}</span>
          <span className="uppercase tracking-wider">Confidential</span>
        </div>
      </div>
      )}

      {/* Review thread sits outside the page container */}
      <div className="mx-auto max-w-[816px] mt-6">
        <ReviewCommentThread artifactType="protocol" artifactId="protocol" />
      </div>

      {/* Version history slide-over */}
      {showVersionHistory && protocolData?.versions && build && (
        <div className="fixed inset-y-0 right-0 z-50 flex">
          <div
            className="flex-1"
            onClick={() => setShowVersionHistory(false)}
          />
          <div className="w-80 border-l border-border bg-card shadow-xl">
            <ProtocolVersionHistory
              versions={protocolData.versions}
              currentVersionId={build.versionId}
              onClose={() => setShowVersionHistory(false)}
            />
          </div>
        </div>
      )}
    </div>
    </div>
    </div>
  );
}

// ── Protocol Document Helpers ────────────────────────

function DocSectionHeading({ number, title, id }: { number: string; title: string; id: string }) {
  return (
    <h2
      id={id}
      className="mt-10 mb-4 text-lg font-bold text-foreground border-b-2 border-primary/20 pb-2 first:mt-0"
    >
      <span className="mr-3 text-primary tabular-nums">{number}</span>
      {title.toUpperCase()}
    </h2>
  );
}

function DocSubSectionHeading({ number, title }: { number: string; title: string }) {
  return (
    <h3 className="mt-6 mb-3 text-base font-semibold text-foreground">
      <span className="mr-2 text-primary/80 tabular-nums">{number}</span>
      {title}
    </h3>
  );
}

function SynopsisRow({ label, value }: { label: string; value?: string | number | null }) {
  if (!value) return null;
  return (
    <tr className="border-b border-border">
      <td className="w-48 border-r border-border bg-muted/30 px-4 py-2.5 text-sm font-semibold text-foreground align-top">
        {label}
      </td>
      <td className="px-4 py-2.5 text-sm text-foreground leading-relaxed">{value}</td>
    </tr>
  );
}

function NoDataPlaceholder() {
  return (
    <p className="text-sm text-muted-foreground italic py-2">No data generated yet.</p>
  );
}

// ── CRF ───────────────────────────────────────────────

export function CrfContent({ artifactId }: { artifactId: string }) {
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const visits = useStudyBuilderStore((s) => s.visits);
  const selectArtifact = useStudyBuilderStore((s) => s.selectArtifact);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);
  const crf = crfs.find((c) => c.id === artifactId);

  const [viewMode, setViewMode] = useState<"structure" | "preview">(
    "structure",
  );
  const [isEditing, setIsEditing] = useState(false);
  const [editSections, setEditSections] = useState<CrfSection[]>([]);
  const [expandedFieldId, setExpandedFieldId] = useState<string | null>(null);

  const updateArtifact = useUpdateBuildArtifact();

  const linkedVisits = useMemo(
    () =>
      crf
        ? visits.filter(
            (v) =>
              v.requiredCrfIds?.includes(crf.id) ||
              v.optionalCrfIds?.includes(crf.id),
          )
        : [],
    [crf, visits],
  );

  if (!crf) {
    return <EmptyState text="CRF not found" />;
  }

  const totalFields = crf.sections.reduce(
    (sum, sec) => sum + sec.fields.length,
    0,
  );

  const startEditing = () => {
    setEditSections(JSON.parse(JSON.stringify(crf.sections)));
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditSections([]);
  };

  const saveEdits = () => {
    const buildId = useStudyBuilderStore.getState().buildId;
    if (!buildId) return;
    updateArtifact.mutate(
      {
        buildId,
        artifactType: "crf",
        artifactId: crf.id,
        updates: { sections: editSections },
      },
      {
        onSuccess: () => setIsEditing(false),
      },
    );
  };

  const moveSectionUp = (idx: number) => {
    if (idx === 0) return;
    const next = [...editSections];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    next.forEach((s, i) => (s.order = i));
    setEditSections(next);
  };

  const moveSectionDown = (idx: number) => {
    if (idx >= editSections.length - 1) return;
    const next = [...editSections];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    next.forEach((s, i) => (s.order = i));
    setEditSections(next);
  };

  const renameSectionAt = (idx: number, name: string) => {
    const next = [...editSections];
    next[idx] = { ...next[idx], name };
    setEditSections(next);
  };

  const moveFieldUp = (sIdx: number, fIdx: number) => {
    if (fIdx === 0) return;
    const next = [...editSections];
    const fields = [...next[sIdx].fields];
    [fields[fIdx - 1], fields[fIdx]] = [fields[fIdx], fields[fIdx - 1]];
    fields.forEach((f, i) => (f.order = i));
    next[sIdx] = { ...next[sIdx], fields };
    setEditSections(next);
  };

  const moveFieldDown = (sIdx: number, fIdx: number) => {
    const section = editSections[sIdx];
    if (fIdx >= section.fields.length - 1) return;
    const next = [...editSections];
    const fields = [...next[sIdx].fields];
    [fields[fIdx], fields[fIdx + 1]] = [fields[fIdx + 1], fields[fIdx]];
    fields.forEach((f, i) => (f.order = i));
    next[sIdx] = { ...next[sIdx], fields };
    setEditSections(next);
  };

  const updateFieldAt = (sIdx: number, fIdx: number, patch: Partial<CrfField>) => {
    const next = [...editSections];
    const fields = [...next[sIdx].fields];
    fields[fIdx] = { ...fields[fIdx], ...patch };
    next[sIdx] = { ...next[sIdx], fields };
    setEditSections(next);
  };

  const deleteFieldAt = (sIdx: number, fIdx: number) => {
    const next = [...editSections];
    const fields = next[sIdx].fields.filter((_, i) => i !== fIdx);
    fields.forEach((f, i) => (f.order = i));
    next[sIdx] = { ...next[sIdx], fields };
    setEditSections(next);
    setExpandedFieldId(null);
  };

  const addFieldToSection = (sIdx: number) => {
    const next = [...editSections];
    const fields = [...next[sIdx].fields];
    const newId = `new_field_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const newField: CrfField = {
      id: newId,
      name: "",
      label: "New Field",
      dataType: "text",
      required: false,
      width: "full",
      aiGenerated: false,
      aiConfidence: 0,
      order: fields.length,
    };
    fields.push(newField);
    next[sIdx] = { ...next[sIdx], fields };
    setEditSections(next);
    setExpandedFieldId(newId);
  };

  const updateSectionAt = (idx: number, patch: Partial<CrfSection>) => {
    const next = [...editSections];
    next[idx] = { ...next[idx], ...patch };
    setEditSections(next);
  };

  const addSection = () => {
    const next = [...editSections];
    next.push({
      id: `new_section_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: "New Section",
      order: next.length,
      fields: [],
    });
    setEditSections(next);
  };

  const deleteSectionAt = (idx: number) => {
    const next = editSections.filter((_, i) => i !== idx);
    next.forEach((s, i) => (s.order = i));
    setEditSections(next);
    setExpandedFieldId(null);
  };

  // Collect all fields across all sections for display condition source picker
  const allFields = useMemo(
    () => editSections.flatMap((s) => s.fields),
    [editSections],
  );

  const sectionsToRender = isEditing ? editSections : crf.sections;

  return (
    <div className="flex flex-col gap-4 p-4">
      <ArtifactReviewBar artifactType="crf" artifactId={crf.id} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            {crf.name}
          </h2>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {crf.category} &middot; {crf.sections.length} section
            {crf.sections.length !== 1 ? "s" : ""} &middot; {totalFields} field
            {totalFields !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={cancelEditing}
                className="flex items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                <X className="h-3 w-3" />
                Cancel
              </button>
              <button
                onClick={saveEdits}
                disabled={updateArtifact.isPending}
                className="flex items-center gap-1 rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Check className="h-3 w-3" />
                Save
              </button>
            </>
          ) : (
            <>
              <StatusBadge status={crf.status} />
              <LockButton artifactType="crf" artifactId={crf.id} locked={crf.locked} />
              {!isReadOnly && !crf.locked && (
                <button
                  onClick={startEditing}
                  className="flex items-center gap-1 rounded-md border-2 border-primary/40 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10"
                  title="Edit CRF"
                >
                  <Pencil className="h-3 w-3" />
                  Edit
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Visit linkage */}
      {linkedVisits.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <Calendar className="h-3 w-3 text-muted-foreground" />
          <span className="text-[10px] font-medium text-muted-foreground">
            Collected at:
          </span>
          {linkedVisits.map((v) => {
            const isRequired = v.requiredCrfIds?.includes(crf.id);
            return (
              <button
                key={v.id}
                onClick={() => selectArtifact("visit", v.id)}
                className={cn(
                  "rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors hover:bg-primary/10 hover:text-primary",
                  isRequired
                    ? "border-border text-foreground"
                    : "border-dashed border-border text-muted-foreground",
                )}
              >
                {v.name}
                {!isRequired && " (optional)"}
              </button>
            );
          })}
        </div>
      )}

      {/* Preview toggle (only when not editing) */}
      {!isEditing && viewMode === "preview" && (
        <button
          onClick={() => setViewMode("structure")}
          className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <List className="h-3 w-3" />
          Structure
        </button>
      )}
      {!isEditing && viewMode === "structure" && (
        <button
          onClick={() => setViewMode("preview")}
          className="flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <Eye className="h-3 w-3" />
          Preview
        </button>
      )}

      {/* Content */}
      {viewMode === "preview" && !isEditing ? (
        <CrfFormPreview sections={crf.sections} />
      ) : (
        <>
          {sectionsToRender.map((section, sIdx) => (
            <div key={section.id} className="rounded-lg border border-border bg-card">
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                {isEditing && (
                  <div className="flex flex-col">
                    <button
                      onClick={() => moveSectionUp(sIdx)}
                      disabled={sIdx === 0}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => moveSectionDown(sIdx)}
                      disabled={sIdx >= sectionsToRender.length - 1}
                      className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                )}
                {isEditing ? (
                  <input
                    value={section.name}
                    onChange={(e) => renameSectionAt(sIdx, e.target.value)}
                    className="flex-1 rounded border border-border bg-background px-2 py-0.5 text-xs font-medium text-foreground"
                  />
                ) : (
                  <h3 className="flex-1 text-sm font-medium text-foreground">
                    {section.name}
                  </h3>
                )}
                {isEditing && (
                  <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                    <input
                      type="checkbox"
                      checked={section.repeating ?? false}
                      onChange={(e) =>
                        updateSectionAt(sIdx, { repeating: e.target.checked })
                      }
                      className="h-3 w-3 rounded border-border text-primary focus:ring-primary/30"
                    />
                    Repeating
                  </label>
                )}
                {isEditing && section.repeating && (
                  <input
                    type="number"
                    value={section.maxRepeat ?? ""}
                    onChange={(e) =>
                      updateSectionAt(sIdx, {
                        maxRepeat: e.target.value ? Number(e.target.value) : undefined,
                      })
                    }
                    className="w-16 rounded border border-border bg-background px-1.5 py-0.5 text-[10px] text-foreground"
                    placeholder="Max"
                    min={1}
                  />
                )}
                {!isEditing && section.repeating && (
                  <span className="flex items-center gap-1 text-[10px] text-muted-foreground">
                    <Repeat className="h-3 w-3" />
                    Repeating{section.maxRepeat ? ` (max ${section.maxRepeat})` : ""}
                  </span>
                )}
                {isEditing && (
                  <button
                    onClick={() => deleteSectionAt(sIdx)}
                    className="text-muted-foreground hover:text-red-600 transition-colors"
                    title="Delete section"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <div className="divide-y divide-border">
                {section.fields.map((field, fIdx) => (
                  <div key={field.id}>
                    <div
                      className={cn(
                        "flex items-center gap-3 px-3 py-2 transition-colors",
                        isEditing && "cursor-pointer hover:bg-muted/50",
                        isEditing && expandedFieldId === field.id && "bg-muted/50",
                        !isEditing && "hover:bg-muted/30",
                      )}
                      onClick={() => {
                        if (!isEditing) return;
                        setExpandedFieldId(
                          expandedFieldId === field.id ? null : field.id,
                        );
                      }}
                    >
                      {isEditing && (
                        <div
                          className="flex flex-col"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => moveFieldUp(sIdx, fIdx)}
                            disabled={fIdx === 0}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          >
                            <ChevronUp className="h-2.5 w-2.5" />
                          </button>
                          <button
                            onClick={() => moveFieldDown(sIdx, fIdx)}
                            disabled={fIdx >= section.fields.length - 1}
                            className="text-muted-foreground hover:text-foreground disabled:opacity-30"
                          >
                            <ChevronDown className="h-2.5 w-2.5" />
                          </button>
                        </div>
                      )}
                      {isEditing && (
                        expandedFieldId === field.id ? (
                          <ChevronDown className="h-3 w-3 shrink-0 text-primary" />
                        ) : (
                          <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                        )
                      )}
                      <span className="flex-1 truncate text-sm text-foreground">
                        {field.label}
                      </span>
                      {/* Property indicator badges */}
                      {field.validation && (
                        <span className="shrink-0 rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[9px] font-medium text-amber-600">
                          validation
                        </span>
                      )}
                      {field.formula && (
                        <span className="shrink-0 rounded-full bg-blue-500/10 px-1.5 py-0.5 text-[9px] font-medium text-blue-600">
                          formula
                        </span>
                      )}
                      {field.displayCondition && (
                        <span className="shrink-0 rounded-full bg-purple-500/10 px-1.5 py-0.5 text-[9px] font-medium text-purple-600">
                          conditional
                        </span>
                      )}
                      {field.choices && field.choices.length > 0 && (
                        <span className="shrink-0 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-medium text-emerald-600">
                          {field.choices.length} choices
                        </span>
                      )}
                      <span className="shrink-0 text-[10px] text-muted-foreground">
                        {field.dataType}
                      </span>
                      {field.required && (
                        <span className="shrink-0 text-[10px] font-medium text-red-500">
                          REQ
                        </span>
                      )}
                    </div>
                    {isEditing && expandedFieldId === field.id && (
                      <CrfFieldEditor
                        field={field}
                        siblingFields={allFields}
                        onChange={(patch) => updateFieldAt(sIdx, fIdx, patch)}
                        onDelete={() => deleteFieldAt(sIdx, fIdx)}
                      />
                    )}
                  </div>
                ))}
              </div>
              {isEditing && (
                <button
                  onClick={() => addFieldToSection(sIdx)}
                  className="flex w-full items-center justify-center gap-1.5 border-t border-dashed border-border py-2 text-[11px] font-medium text-muted-foreground hover:bg-muted/50 hover:text-primary transition-colors"
                >
                  <Plus className="h-3 w-3" />
                  Add field
                </button>
              )}
            </div>
          ))}
          {isEditing && (
            <button
              onClick={addSection}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border py-3 text-[11px] font-medium text-muted-foreground hover:border-primary/50 hover:bg-primary/5 hover:text-primary transition-colors"
            >
              <Plus className="h-3.5 w-3.5" />
              Add section
            </button>
          )}
        </>
      )}

      <ReviewCommentThread artifactType="crf" artifactId={crf.id} />
    </div>
  );
}

// ── CRF Form Preview ─────────────────────────────────

function CrfFormPreview({ sections }: { sections: CrfSection[] }) {
  return (
    <div className="flex flex-col gap-5">
      {sections.map((section) => (
        <div
          key={section.id}
          className="rounded-lg border border-border bg-card"
        >
          <div className="flex items-center gap-2 border-b border-border bg-muted/30 px-4 py-2.5">
            <h3 className="text-xs font-semibold text-foreground">
              {section.name}
            </h3>
            {section.repeating && (
              <span className="flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground">
                <Repeat className="h-3 w-3" />
                Repeating
              </span>
            )}
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 gap-4">
              {section.fields.map((field) => (
                <div
                  key={field.id}
                  className={cn(
                    field.width === "half" ? "col-span-1" : "",
                    field.width === "third" ? "col-span-1" : "",
                  )}
                >
                  <FormFieldPreview field={field} />
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FormFieldPreview({ field }: { field: CrfField }) {
  const label = (
    <label className="mb-1 flex items-center gap-1 text-xs font-medium text-foreground">
      {field.label}
      {field.required && <span className="text-red-500">*</span>}
      {field.unit && (
        <span className="text-[10px] text-muted-foreground">({field.unit})</span>
      )}
      {field.displayCondition && (
        <span title="Conditional"><Info className="h-3 w-3 text-muted-foreground" /></span>
      )}
    </label>
  );

  const hint = field.hintLabel ? (
    <p className="mt-0.5 text-[10px] text-muted-foreground">
      {field.hintLabel}
    </p>
  ) : null;

  const inputClasses =
    "w-full rounded-md border border-border bg-muted/20 px-3 py-1.5 text-xs text-muted-foreground";

  switch (field.dataType) {
    case "text":
      return (
        <div>
          {label}
          <input
            type="text"
            disabled
            placeholder={field.label}
            className={inputClasses}
          />
          {hint}
        </div>
      );
    case "number":
    case "integer":
      return (
        <div>
          {label}
          <input
            type="number"
            disabled
            placeholder={
              field.validation?.min != null
                ? `${field.validation.min}–${field.validation.max ?? ""}`
                : field.label
            }
            className={inputClasses}
          />
          {hint}
        </div>
      );
    case "date":
    case "datetime":
    case "time":
      return (
        <div>
          {label}
          <input
            type="text"
            disabled
            placeholder={field.hintLabel || "DD-MMM-YYYY"}
            className={inputClasses}
          />
          {hint}
        </div>
      );
    case "textarea":
      return (
        <div>
          {label}
          <textarea
            disabled
            rows={2}
            placeholder={field.label}
            className={cn(inputClasses, "resize-none")}
          />
          {hint}
        </div>
      );
    case "select":
    case "multi_select":
      return (
        <div>
          {label}
          <select disabled className={inputClasses}>
            <option>Select...</option>
            {field.choices?.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
          {hint}
        </div>
      );
    case "radio":
      return (
        <div>
          {label}
          <div className="mt-1 flex flex-wrap gap-3">
            {field.choices?.map((c) => (
              <label
                key={c.value}
                className="flex items-center gap-1.5 text-xs text-muted-foreground"
              >
                <input type="radio" disabled className="h-3 w-3" />
                {c.label}
              </label>
            )) ?? (
              <span className="text-[10px] text-muted-foreground">
                No options
              </span>
            )}
          </div>
          {hint}
        </div>
      );
    case "boolean":
      return (
        <div>
          {label}
          <div className="mt-1 flex gap-3">
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="radio" disabled className="h-3 w-3" />
              Yes
            </label>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <input type="radio" disabled className="h-3 w-3" />
              No
            </label>
          </div>
          {hint}
        </div>
      );
    case "calculated":
      return (
        <div>
          {label}
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/10 px-3 py-1.5 text-[10px] text-muted-foreground">
            Calculated{" "}
            {field.formula?.clinicalPreset
              ? `(${field.formula.clinicalPreset})`
              : ""}
          </div>
          {hint}
        </div>
      );
    case "file":
      return (
        <div>
          {label}
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-[10px] text-muted-foreground">
            File upload
          </div>
          {hint}
        </div>
      );
    case "signature":
      return (
        <div>
          {label}
          <div className="flex items-center gap-2 rounded-md border border-dashed border-border px-3 py-2 text-[10px] text-muted-foreground">
            Signature capture
          </div>
          {hint}
        </div>
      );
    default:
      return (
        <div>
          {label}
          <input
            type="text"
            disabled
            placeholder={field.label}
            className={inputClasses}
          />
          {hint}
        </div>
      );
  }
}

// ── Visits ────────────────────────────────────────────

export function VisitContent({ artifactId }: { artifactId: string }) {
  const visits = useStudyBuilderStore((s) => s.visits);
  const crfs = useStudyBuilderStore((s) => s.crfs);
  const arms = useStudyBuilderStore((s) => s.arms);
  const selectArtifact = useStudyBuilderStore((s) => s.selectArtifact);

  if (visits.length === 0) return <EmptyState text="No visits yet" />;

  // If a specific visit is selected, show its detail
  const selectedVisit = visits.find((v) => v.id === artifactId);

  if (selectedVisit) {
    return (
      <SingleVisitDetail
        visit={selectedVisit}
        crfs={crfs}
        arms={arms}
        selectArtifact={selectArtifact}
      />
    );
  }

  // Otherwise show the visit table
  return (
    <div className="p-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                #
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                Visit
              </th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                Type
              </th>
              <th className="px-3 py-2 text-right font-medium text-muted-foreground">
                Day
              </th>
              <th className="px-3 py-2 text-center font-medium text-muted-foreground">
                Window
              </th>
              <th className="px-3 py-2 text-center font-medium text-muted-foreground">
                CRFs
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visits.map((visit) => (
              <tr
                key={visit.id}
                className="cursor-pointer transition-colors hover:bg-muted/30"
                onClick={() => selectArtifact("visit", visit.id)}
              >
                <td className="px-3 py-2 tabular-nums text-muted-foreground">
                  {visit.visitNumber}
                </td>
                <td className="px-3 py-2 font-medium text-foreground">
                  {visit.name}
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
                    {visit.type.replace("_", " ")}
                  </span>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-foreground">
                  {visit.studyDay ?? "\u2014"}
                </td>
                <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">
                  -{visit.windowDaysBefore}/+{visit.windowDaysAfter}d
                </td>
                <td className="px-3 py-2 text-center tabular-nums text-muted-foreground">
                  {(visit.requiredCrfIds?.length ?? 0) +
                    (visit.optionalCrfIds?.length ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SingleVisitDetail({
  visit,
  crfs,
  arms,
  selectArtifact,
}: {
  visit: VisitDefinition;
  crfs: CrfDefinition[];
  arms: { id: string; name: string }[];
  selectArtifact: (type: ArtifactType, id: string) => void;
}) {
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(visit.name);
  const [editDay, setEditDay] = useState(String(visit.studyDay ?? ""));
  const [editWindowBefore, setEditWindowBefore] = useState(
    String(visit.windowDaysBefore),
  );
  const [editWindowAfter, setEditWindowAfter] = useState(
    String(visit.windowDaysAfter),
  );

  const updateArtifact = useUpdateBuildArtifact();

  const requiredCrfs = useMemo(
    () => crfs.filter((c) => visit.requiredCrfIds?.includes(c.id)),
    [crfs, visit.requiredCrfIds],
  );
  const optionalCrfs = useMemo(
    () => crfs.filter((c) => visit.optionalCrfIds?.includes(c.id)),
    [crfs, visit.optionalCrfIds],
  );
  const applicableArms = useMemo(
    () => arms.filter((a) => visit.applicableArms?.includes(a.id)),
    [arms, visit.applicableArms],
  );

  const startEditing = () => {
    setEditName(visit.name);
    setEditDay(String(visit.studyDay ?? ""));
    setEditWindowBefore(String(visit.windowDaysBefore));
    setEditWindowAfter(String(visit.windowDaysAfter));
    setIsEditing(true);
  };

  const saveEdits = () => {
    const buildId = useStudyBuilderStore.getState().buildId;
    if (!buildId) return;
    updateArtifact.mutate(
      {
        buildId,
        artifactType: "visit",
        artifactId: visit.id,
        updates: {
          name: editName,
          studyDay: editDay ? Number(editDay) : null,
          windowDaysBefore: Number(editWindowBefore) || 0,
          windowDaysAfter: Number(editWindowAfter) || 0,
        },
      },
      {
        onSuccess: () => setIsEditing(false),
      },
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <ArtifactReviewBar artifactType="visit" artifactId={visit.id} />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          {isEditing ? (
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="rounded border border-border bg-background px-2 py-1 text-base font-semibold text-foreground"
            />
          ) : (
            <h2 className="text-base font-semibold text-foreground">
              {visit.name}
            </h2>
          )}
          <div className="mt-1 flex items-center gap-2">
            <span className="inline-flex rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
              {visit.type.replace("_", " ")}
            </span>
            {isEditing ? (
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
                Day{" "}
                <input
                  value={editDay}
                  onChange={(e) => setEditDay(e.target.value)}
                  className="w-12 rounded border border-border bg-background px-1 py-0.5 text-[10px]"
                />
                &middot; Window -{" "}
                <input
                  value={editWindowBefore}
                  onChange={(e) => setEditWindowBefore(e.target.value)}
                  className="w-8 rounded border border-border bg-background px-1 py-0.5 text-[10px]"
                />
                /+
                <input
                  value={editWindowAfter}
                  onChange={(e) => setEditWindowAfter(e.target.value)}
                  className="w-8 rounded border border-border bg-background px-1 py-0.5 text-[10px]"
                />
                d
              </div>
            ) : (
              <span className="text-[10px] text-muted-foreground">
                Day {visit.studyDay ?? "—"} &middot; Window -{" "}
                {visit.windowDaysBefore}/+{visit.windowDaysAfter}d
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted"
              >
                <X className="h-3 w-3" />
                Cancel
              </button>
              <button
                onClick={saveEdits}
                disabled={updateArtifact.isPending}
                className="flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                <Check className="h-3 w-3" />
                Save
              </button>
            </>
          ) : (
            !isReadOnly && (
              <button
                onClick={startEditing}
                className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <Pencil className="h-3 w-3" />
                Edit
              </button>
            )
          )}
        </div>
      </div>

      {/* Required CRFs */}
      {requiredCrfs.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold text-foreground">
            Required Forms ({requiredCrfs.length})
          </h3>
          <div className="flex flex-col gap-1">
            {requiredCrfs.map((c) => (
              <button
                key={c.id}
                onClick={() => selectArtifact("crf", c.id)}
                className="flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                <span className="flex-1 truncate text-xs font-medium text-foreground">
                  {c.name}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {c.sections.reduce((s, sec) => s + sec.fields.length, 0)}{" "}
                  fields
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Optional CRFs */}
      {optionalCrfs.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold text-muted-foreground">
            Optional Forms ({optionalCrfs.length})
          </h3>
          <div className="flex flex-col gap-1">
            {optionalCrfs.map((c) => (
              <button
                key={c.id}
                onClick={() => selectArtifact("crf", c.id)}
                className="flex items-center gap-2 rounded-md border border-dashed border-border bg-card px-3 py-2 text-left transition-colors hover:border-primary/40 hover:bg-primary/5"
              >
                <FileText className="h-3.5 w-3.5 shrink-0 text-blue-500/50" />
                <span className="flex-1 truncate text-xs text-muted-foreground">
                  {c.name}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground">
                  {c.sections.reduce((s, sec) => s + sec.fields.length, 0)}{" "}
                  fields
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Applicable Arms */}
      {applicableArms.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-xs font-semibold text-muted-foreground">
            Applicable Arms
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {applicableArms.map((a) => (
              <button
                key={a.id}
                onClick={() => selectArtifact("arm", a.id)}
                className="flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-[10px] font-medium text-foreground transition-colors hover:bg-primary/10"
              >
                <GitBranch className="h-3 w-3" />
                {a.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {requiredCrfs.length === 0 && optionalCrfs.length === 0 && (
        <p className="text-xs italic text-muted-foreground/60">
          No CRFs assigned to this visit yet.
        </p>
      )}

      <ReviewCommentThread artifactType="visit" artifactId={visit.id} />
    </div>
  );
}

// ── Arms ──────────────────────────────────────────────

function ArmContent() {
  const arms = useStudyBuilderStore((s) => s.arms);

  if (arms.length === 0) return <EmptyState text="No treatment arms yet" />;

  return (
    <div className="flex flex-col gap-3 p-4">
      <ArtifactReviewBar artifactType="arm" artifactId="all" />

      {arms.map((arm) => (
        <div
          key={arm.id}
          className="flex flex-col gap-2 rounded-lg border border-border bg-card p-3"
        >
          <div className="flex items-start justify-between">
            <h3 className="text-xs font-semibold text-foreground">
              {arm.name}
            </h3>
            <span className="inline-flex rounded-full border border-border bg-muted px-1.5 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
              {arm.type.replace("_", " ")}
            </span>
          </div>
          <p className="text-xs text-muted-foreground">{arm.intervention}</p>
          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
            {arm.dosage && <span>Dose: {arm.dosage}</span>}
            {arm.targetN != null && <span>N={arm.targetN}</span>}
            {arm.randomizationRatio && (
              <span>Ratio: {arm.randomizationRatio}</span>
            )}
          </div>
        </div>
      ))}

      <ReviewCommentThread artifactType="arm" artifactId="all" />
    </div>
  );
}

// ── Eligibility ───────────────────────────────────────

function EligibilityContent() {
  const criteria = useStudyBuilderStore((s) => s.eligibilityCriteria);

  if (criteria.length === 0)
    return <EmptyState text="No eligibility criteria yet" />;

  const inclusion = criteria.filter((c) => c.type === "inclusion");
  const exclusion = criteria.filter((c) => c.type === "exclusion");

  return (
    <div className="flex flex-col gap-4 p-4">
      <ArtifactReviewBar artifactType="eligibility" artifactId="all" />

      {[
        { title: "Inclusion", items: inclusion, color: "text-emerald-600" },
        { title: "Exclusion", items: exclusion, color: "text-red-600" },
      ].map((group) =>
        group.items.length > 0 ? (
          <div key={group.title}>
            <h3 className="mb-2 text-xs font-semibold text-foreground">
              {group.title} ({group.items.length})
            </h3>
            <div className="flex flex-col gap-1.5">
              {group.items.map((criterion) => (
                <div
                  key={criterion.id}
                  className="flex gap-2 rounded-lg border border-border bg-card px-3 py-2"
                >
                  <span
                    className={cn(
                      "mt-0.5 shrink-0 text-[10px] font-bold tabular-nums",
                      group.color,
                    )}
                  >
                    {criterion.number}.
                  </span>
                  <p className="text-xs text-foreground">{criterion.text}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null,
      )}

      <ReviewCommentThread artifactType="eligibility" artifactId="all" />
    </div>
  );
}

// ── Endpoints ─────────────────────────────────────────

function EndpointContent() {
  const endpoints = useStudyBuilderStore((s) => s.endpoints);

  const grouped = useMemo(() => {
    const map: Record<string, typeof endpoints> = {};
    for (const ep of endpoints) {
      if (!map[ep.type]) map[ep.type] = [];
      map[ep.type].push(ep);
    }
    return map;
  }, [endpoints]);

  if (endpoints.length === 0) return <EmptyState text="No endpoints yet" />;

  const typeOrder = ["primary", "secondary", "exploratory", "safety"] as const;
  const TYPE_COLORS: Record<string, string> = {
    primary: "border-l-primary",
    secondary: "border-l-violet-500",
    exploratory: "border-l-amber-500",
    safety: "border-l-red-500",
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <ArtifactReviewBar artifactType="endpoint" artifactId="all" />

      {typeOrder
        .filter((t) => grouped[t]?.length)
        .map((type) => (
          <div key={type}>
            <h3 className="mb-2 text-xs font-semibold capitalize text-foreground">
              {type} ({grouped[type].length})
            </h3>
            <div className="flex flex-col gap-2">
              {grouped[type].map((ep) => (
                <div
                  key={ep.id}
                  className={cn(
                    "rounded-lg border border-l-4 border-border bg-card p-3",
                    TYPE_COLORS[type] ?? "",
                  )}
                >
                  <h4 className="text-xs font-medium text-foreground">
                    {ep.name}
                  </h4>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {ep.description}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                    <span>
                      <span className="font-medium">Measure:</span> {ep.measure}
                    </span>
                    <span>
                      <span className="font-medium">Timeframe:</span>{" "}
                      {ep.timeframe}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

      <ReviewCommentThread artifactType="endpoint" artifactId="all" />
    </div>
  );
}

// ── eTMF ──────────────────────────────────────────────

export function EtmfContent() {
  const folders = useStudyBuilderStore((s) => s.etmfFolders);
  const etmfDocuments = useStudyBuilderStore((s) => s.etmfDocuments);
  const buildId = useStudyBuilderStore((s) => s.buildId);
  const setEtmfFilingFile = useStudyBuilderStore((s) => s.setEtmfFilingFile);
  const setEtmfFilingSuggestion = useStudyBuilderStore((s) => s.setEtmfFilingSuggestion);
  const selectArtifact = useStudyBuilderStore((s) => s.selectArtifact);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const dragCounterRef = { current: 0 };

  const completion = useMemo(
    () => computeEtmfCompletion(folders, etmfDocuments),
    [folders, etmfDocuments],
  );

  const docIndex = useMemo(() => {
    const map = new Map<string, typeof etmfDocuments>();
    for (const doc of etmfDocuments) {
      const key = `${doc.folderId}::${doc.expectedDocName}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(doc);
    }
    return map;
  }, [etmfDocuments]);

  const grouped = useMemo(() => {
    const map: Record<string, typeof folders> = {};
    for (const folder of folders) {
      if (!map[folder.zone]) map[folder.zone] = [];
      map[folder.zone].push(folder);
    }
    return map;
  }, [folders]);

  const zones = useMemo(() => Object.keys(grouped), [grouped]);

  // Auto-select first zone
  useEffect(() => {
    if (zones.length > 0 && !selectedZone) setSelectedZone(zones[0]);
  }, [zones, selectedZone]);

  const activeFolders = selectedZone ? (grouped[selectedZone] ?? []) : [];
  const activeFolder = activeFolders.find((f) => f.id === selectedFolderId) ?? null;

  if (folders.length === 0) return <EmptyState text="No eTMF structure yet" />;

  const handleFileForDoc = (folderId: string, expectedDocName: string) => {
    const input = document.createElement("input");
    input.type = "file";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) {
        setEtmfFilingFile(file);
        setEtmfFilingSuggestion({ folderId, expectedDocName });
      }
    };
    input.click();
  };

  const handleDocColumnDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragOverFolderId(null);

    if (isReadOnly || !activeFolder) return;
    const file = e.dataTransfer.files[0];
    if (!file) return;

    setEtmfFilingFile(file);
    setEtmfFilingSuggestion(null);

    const folderId = activeFolder.id;
    if (buildId) {
      suggestEtmfFilingFn({ buildId, filename: file.name, mimeType: file.type || "" })
        .then((result) => setEtmfFilingSuggestion({ ...result, folderId }))
        .catch(() => {
          const fallback = suggestFolder(file.name, folders);
          setEtmfFilingSuggestion(fallback ? { ...fallback, folderId } : { folderId, expectedDocName: "" });
        });
    } else {
      setEtmfFilingSuggestion({ folderId, expectedDocName: "" });
    }
  };

  return (
    <div className="flex h-full">
      {/* Column 1: Zones */}
      <div className="flex w-52 shrink-0 flex-col border-r border-border">
        <div className="border-b border-border px-3 py-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Zones</span>
        </div>
        <div className="flex-1 overflow-y-auto p-1.5">
          {zones.map((zone) => {
            const zoneComp = completion.byZone[zone];
            const isActive = selectedZone === zone;
            return (
              <button
                key={zone}
                onClick={() => { setSelectedZone(zone); setSelectedFolderId(null); }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors",
                  isActive
                    ? "bg-primary/10 text-primary"
                    : "text-foreground hover:bg-muted",
                )}
              >
                <FolderTree className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-medium">{zone}</span>
                  {zoneComp && (
                    <span className="block text-[10px] text-muted-foreground">
                      {zoneComp.filed}/{zoneComp.expected} filed
                    </span>
                  )}
                </div>
                <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />
              </button>
            );
          })}
        </div>
        {/* Compact completion */}
        {completion.totalExpected > 0 && (
          <div className="border-t border-border px-3 py-2">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <span>{completion.totalFiled}/{completion.totalExpected} filed</span>
              <span>{completion.completionPercent}%</span>
            </div>
            <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${completion.completionPercent}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Column 2: Folders in selected zone */}
      {selectedZone && (
        <div className="flex w-56 shrink-0 flex-col border-r border-border">
          <div className="border-b border-border px-3 py-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Folders</span>
          </div>
          <div className="flex-1 overflow-y-auto p-1.5">
            {activeFolders.map((folder) => {
              const isActive = selectedFolderId === folder.id;
              const folderFiled = folder.expectedDocuments.filter(
                (d) => docIndex.has(`${folder.id}::${d.name}`),
              ).length;
              return (
                <button
                  key={folder.id}
                  onClick={() => {
                    setSelectedFolderId(folder.id);
                    selectArtifact("etmf", folder.id);
                  }}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-foreground hover:bg-muted",
                  )}
                >
                  <Folder className="h-3.5 w-3.5 shrink-0 text-amber-500/70" />
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">
                      {folder.section}
                    </span>
                    <span className="block truncate text-[10px] text-muted-foreground">
                      {folder.artifact}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <span className="text-[10px] text-muted-foreground">
                      {folderFiled}/{folder.expectedDocuments.length}
                    </span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Column 3: Documents in selected folder (drop target) */}
      {activeFolder ? (
        <div
          className={cn(
            "flex flex-1 flex-col overflow-hidden transition-colors",
            dragOverFolderId === activeFolder.id && "bg-primary/5",
          )}
          onDragEnter={(e) => {
            e.preventDefault();
            dragCounterRef.current++;
            if (dragCounterRef.current === 1) setDragOverFolderId(activeFolder.id);
          }}
          onDragLeave={(e) => {
            e.preventDefault();
            dragCounterRef.current--;
            if (dragCounterRef.current <= 0) { dragCounterRef.current = 0; setDragOverFolderId(null); }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDocColumnDrop}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-2">
            <div>
              <span className="text-xs font-semibold text-foreground">{activeFolder.artifact}</span>
              <span className="ml-2 text-[10px] text-muted-foreground">{activeFolder.section}</span>
            </div>
            <ArtifactReviewBar artifactType="etmf" artifactId={activeFolder.id} />
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {dragOverFolderId === activeFolder.id && (
              <div className="mb-3 flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 py-4 text-primary">
                <Upload className="h-4 w-4" />
                <span className="text-xs font-medium">Drop file to auto-file here</span>
              </div>
            )}

            {activeFolder.expectedDocuments.map((doc) => {
              const key = `${activeFolder.id}::${doc.name}`;
              const filedDocs = docIndex.get(key);
              const isFiled = !!filedDocs && filedDocs.length > 0;

              return (
                <div
                  key={doc.name}
                  className="flex items-center gap-2.5 rounded-md px-3 py-2 transition-colors hover:bg-muted/50"
                >
                  {isFiled ? (
                    <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
                      <Check className="h-2.5 w-2.5 text-emerald-500" />
                    </span>
                  ) : doc.required ? (
                    <span className="h-4 w-4 shrink-0 rounded-full border-2 border-red-400" />
                  ) : (
                    <span className="h-4 w-4 shrink-0 rounded-full border border-border" />
                  )}
                  <File className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60" />
                  <div className="min-w-0 flex-1">
                    <span className={cn("block truncate text-xs font-medium", isFiled ? "text-foreground" : "text-muted-foreground")}>
                      {doc.name}
                    </span>
                    {isFiled && filedDocs && (
                      <span className="block truncate text-[10px] text-emerald-600">
                        {filedDocs[0].originalFilename}
                      </span>
                    )}
                  </div>
                  {doc.required && !isFiled && (
                    <span className="shrink-0 rounded bg-red-500/10 px-1.5 py-0.5 text-[10px] font-semibold text-red-500">
                      REQ
                    </span>
                  )}
                  {!isFiled && !isReadOnly && (
                    <button
                      onClick={() => handleFileForDoc(activeFolder.id, doc.name)}
                      className="shrink-0 rounded-md bg-primary/10 px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/20"
                    >
                      File
                    </button>
                  )}
                </div>
              );
            })}

            <div className="mt-4">
              <ReviewCommentThread artifactType="etmf" artifactId={activeFolder.id} />
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-xs text-muted-foreground">
          {selectedZone ? "Select a folder" : "Select a zone"}
        </div>
      )}
    </div>
  );
}

// ── Shared ────────────────────────────────────────────

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex h-full items-center justify-center p-6 text-xs text-muted-foreground">
      {text}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium capitalize text-muted-foreground">
      {status}
    </span>
  );
}
